import {
  CalendarClock,
  CheckSquare2,
  MessageSquareText,
  Phone,
  UserRound
} from 'lucide-react'
import type { LiverOverview } from '../lib/liverOverview'

type LiverOverviewSummaryProps = {
  overview: LiverOverview
  loading: boolean
  lineImportsLoading: boolean
  lineImportsError: Error | null
  lineImportsEnabled: boolean
}

export function LiverOverviewSummary({
  overview,
  loading,
  lineImportsLoading,
  lineImportsError,
  lineImportsEnabled
}: LiverOverviewSummaryProps) {
  const items = [
    {
      label: '担当',
      value: loading ? '読み込み中…' : overview.managerName,
      detail: 'current manager',
      icon: UserRound
    },
    {
      label: '通話',
      value: loading ? '—' : `${overview.callCount}件`,
      detail: '保存済み通話',
      icon: Phone
    },
    {
      label: '未完了TODO',
      value: loading ? '—' : `${overview.openTodoCount}件`,
      detail: overview.openTodoCount > 0 ? '対応待ち' : '未完了なし',
      icon: CheckSquare2
    },
    {
      label: '次回フォロー',
      value: loading ? '—' : formatNextFollow(overview.nextFollowDate),
      detail: followDetail(overview.nextFollowState),
      icon: CalendarClock
    },
    {
      label: 'LINE履歴',
      value: lineImportsValue({
        overview,
        loading: lineImportsLoading,
        error: lineImportsError,
        enabled: lineImportsEnabled
      }),
      detail: lineImportsDetail({
        overview,
        loading: lineImportsLoading,
        error: lineImportsError,
        enabled: lineImportsEnabled
      }),
      icon: MessageSquareText
    }
  ]

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-700">運用サマリー</h2>
          <p className="mt-1 text-xs text-gray-400">
            日次確認に必要な担当・通話・TODO・フォロー・LINE状況です。
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2 lg:grid-cols-5">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <div
              key={item.label}
              className="min-w-0 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3"
            >
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-gray-500">
                <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span className="truncate">{item.label}</span>
              </div>
              <div className="truncate text-base font-semibold text-gray-900">
                {item.value}
              </div>
              <div className="mt-1 truncate text-[11px] text-gray-400">{item.detail}</div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function formatNextFollow(date: string | null): string {
  if (!date) return '未設定'
  return new Date(`${date}T00:00:00`).toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    weekday: 'short'
  })
}

function followDetail(state: LiverOverview['nextFollowState']): string {
  switch (state) {
    case 'today':
      return '今日'
    case 'overdue':
      return '要確認'
    case 'upcoming':
      return '予定'
    default:
      return '予定なし'
  }
}

function lineImportsValue({
  overview,
  loading,
  error,
  enabled
}: {
  overview: LiverOverview
  loading: boolean
  error: Error | null
  enabled: boolean
}): string {
  if (!enabled) return '未設定'
  if (loading) return '読み込み中…'
  if (error) return '取得失敗'
  return `${overview.lineImportCount ?? 0}件`
}

function lineImportsDetail({
  overview,
  loading,
  error,
  enabled
}: {
  overview: LiverOverview
  loading: boolean
  error: Error | null
  enabled: boolean
}): string {
  if (!enabled) return 'Internal API未設定'
  if (loading) return '保存履歴を確認中'
  if (error) return error.message
  return `${overview.lineMessageCount ?? 0}メッセージ`
}
